/**
 * TractLayer.tsx — NJ census-tract choropleth layer (MAP-01/MAP-03/MAP-04)
 *
 * Adds a GeoJSON source + fill choropleth layer + outline line layer to the
 * MapLibre GL map.  The choropleth colours are interpolated on the `lvi` field
 * (Opportunity Score) so the proprietary IP is visible on initial load (MAP-04)
 * before any address search.
 *
 * Source/layer lifecycle (Pitfall 5 guard):
 *  - `initializedRef` prevents double-init on React StrictMode re-mount.
 *  - Cleanup resets the ref so a full unmount→remount re-initialises correctly.
 *  - `getLayer` / `getSource` guards on cleanup prevent errors if the map was
 *    removed before the effect cleanup ran.
 *
 * Bbox fetch:
 *  - On every `bbox` prop change (set by MapView on 'load' and 'moveend') the
 *    component fetches /api/tracts/bbox via authedFetch and calls source.setData.
 *  - Rendering into the WebGL canvas is the map's job; this component returns null.
 */
import { useEffect, useRef } from 'react'
import maplibregl from 'maplibre-gl'
import type { GeoJSONSource, MapLayerMouseEvent } from 'maplibre-gl'
import { useMap } from './MapContext'
import { authedFetch } from '../../lib/api'
import { isMapAlive } from '../../lib/mapLiveness'
import type { CensusTract } from '../../lib/tractModel'

// ─── T-275 / BEAD-0097 F2: burst-resilience ───────────────────────────────────
// Rapid pan/zoom fires 'moveend' many times in quick succession. Debouncing the fetch
// means only the FINAL settled extent is requested. TractLayer was the ONLY bbox layer
// without this — StateLayer, CountyLayer and RoadsLayer have all carried it since
// BEAD-0030 — despite being the heaviest tier AND the only one always served dynamically
// (the aggregate tiers fall back to PMTiles; the tract tier never does). Measured before
// this fix: a 12-move pan storm at NYC z11 fired 11 requests, ~9.9 MiB decoded in 1.67s.
// Same 200ms window as the sibling layers: deliberately identical, not independently tuned.
const DEBOUNCE_MS = 200

/** The bbox response shape. `truncated` is set when the viewport exceeded the server cap. */
type TractFeatureCollection = GeoJSON.FeatureCollection & { truncated?: boolean }

export interface TractLayerProps {
  /** Current viewport bbox as [west, south, east, north].  null before map loads. */
  bbox: [number, number, number, number] | null
  /**
   * T-275/F3: called with the server's `truncated` flag after each settled fetch.
   * True ⇒ the viewport exceeded the tract cap and the map is showing only the first
   * TRACT_BBOX_LIMIT tracts, so the caller should tell the user rather than let an
   * incomplete map look complete. Also called with false when the tier goes invisible,
   * so a stale notice cannot outlive it.
   *
   * `shown` is the number of features actually rendered, taken from the payload
   * itself rather than from a copy of the server's cap — the client must not
   * duplicate TRACT_BBOX_LIMIT, which lives in the API and can change independently.
   *
   * NOTE: the API reports only WHETHER the viewport was capped, not how many tracts
   * actually matched — it fetches cap+1 purely to detect the overflow and drops the
   * extra row. So a caller can honestly say "the first N", never "N of M".
   */
  onTruncatedChange?: (truncated: boolean, shown: number) => void
  /** Optional click handler — called with the clicked tract's properties. */
  onTractClick?: (tract: CensusTract) => void
  /** T-012/W1: layer visibility toggle (LayersControl "Score Tracts" row). Default on. */
  visible?: boolean
  /**
   * T-012/W4: opportunity-tier filter — array of tier ids (TRACT_TIERS) to KEEP.
   * Tier is derived from the `lvi` value. Omitted/full ⇒ no tier constraint.
   */
  tierFilter?: string[]
  /**
   * T-012/W4: land-use filter — array of `dominant_type` values to KEEP.
   * Omitted/full ⇒ no type constraint.
   */
  typeFilter?: string[]
  /**
   * T-010: geometry detail level requested from the API, chosen by zoom
   * ('simplified' at mid zoom for a lighter payload, 'full' at high zoom). Omitted ⇒
   * the API default (simplified).
   */
  detail?: 'simplified' | 'full'
}

const SOURCE_ID = 'tracts'
const LAYER_FILL_ID = 'tracts-fill'
const LAYER_OUTLINE_ID = 'tracts-outline'

/**
 * Canonical opportunity-tier ids, high→low. The single source of truth shared by
 * MapShell (initial filter state) and the LayersControl popover. Tiers map to lvi
 * thresholds via TIER_EXPR below (Prime ≥70, Strong ≥60, Moderate ≥45, Marginal ≥35, Low <35).
 */
export const TRACT_TIERS = ['high', 'good', 'moderate', 'low', 'poor'] as const

/** Canonical `dominant_type` land-use classifications. */
export const TRACT_TYPES = [
  'Corridor',
  'Retail',
  'Residential_MFH',
  'Suburban_SFH',
  'Non-Residential',
] as const

/**
 * MapLibre `step` expression deriving a tier id from the `lvi` score. Mirrors the
 * choropleth thresholds so the filter and the colours stay in lock-step.
 */
const TIER_EXPR: unknown[] = [
  'step',
  ['get', 'lvi'],
  'poor', // < 35
  35,
  'low', // ≥ 35
  45,
  'moderate', // ≥ 45
  60,
  'good', // ≥ 60
  70,
  'high', // ≥ 70
]

export function TractLayer({
  bbox,
  onTractClick,
  onTruncatedChange,
  visible = true,
  tierFilter,
  typeFilter,
  detail,
}: TractLayerProps): null {
  const map = useMap()
  const initializedRef = useRef(false)

  // Callers pass an inline arrow (MapShell does), so this prop gets a new identity on
  // every parent render. Keying the fetch effect on it would abort an in-flight tract
  // request and restart the debounce whenever ANY unrelated MapShell state changed —
  // a toggle, a panel, a pin. Hold it in a ref: the effect stays keyed on real inputs
  // (map/bbox/detail/visible) and always calls the latest callback.
  const onTruncatedChangeRef = useRef(onTruncatedChange)
  useEffect(() => {
    onTruncatedChangeRef.current = onTruncatedChange
  }, [onTruncatedChange])

  // -------------------------------------------------------------------------
  // One-time: add GeoJSON source + fill layer + outline layer (Pitfall 5)
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!map || initializedRef.current) return
    initializedRef.current = true

    map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })

    // Choropleth fill — interpolate lvi (0–100) through proprietary colour stops
    map.addLayer({
      id: LAYER_FILL_ID,
      type: 'fill',
      source: SOURCE_ID,
      paint: {
        'fill-color': [
          'interpolate',
          ['linear'],
          ['get', 'lvi'],
          0,
          '#ef4444', // Unfavourable
          35,
          '#f97316', // Marginal
          45,
          '#f59e0b', // Moderate
          60,
          '#4ade80', // Strong
          70,
          '#15803d', // Prime Opportunity
        ],
        'fill-opacity': 0.65,
      },
    })

    map.addLayer({
      id: LAYER_OUTLINE_ID,
      type: 'line',
      source: SOURCE_ID,
      paint: {
        'line-color': '#ffffff',
        'line-width': 0.5,
        'line-opacity': 0.4,
      },
    })

    // T-012/W1: hover tooltip — score + tract id on a teal-accented popup (desktop hover).
    const hoverPopup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 10,
    })

    // T-392: `mousemove`, NOT `mouseenter`. MapLibre's enter/leave pair is LAYER-scoped, so
    // moving from one polygon into the adjacent one never leaves this fill layer and neither
    // event fires — the popup keeps showing whichever feature the pointer first entered, until
    // it exits the layer entirely. Reproduced on the real state pyramid: a 27-step sweep across
    // the PA/NJ border produced exactly ONE mouseenter while the feature underneath changed.
    let shownKey: string | null = null
    let added = false

    const handleMove = (e: MapLayerMouseEvent) => {
      map.getCanvas().style.cursor = 'pointer'
      const f = e.features?.[0]
      if (!f?.properties) return
      const p = f.properties as Record<string, unknown>
      const score = Math.round(Number(p['lvi'] ?? 0))
      const tractId = String(p['tract_geoid'] ?? '')

      // Position tracks the cursor on every move; the HTML is rebuilt only when the feature
      // under it actually changes, so identical markup is not re-parsed ~60x/second.
      hoverPopup.setLngLat(e.lngLat)
      const key = `${tractId}|${score}`
      if (key !== shownKey) {
        shownKey = key
        hoverPopup.setHTML(
          `<div style="font-family:Inter,sans-serif;font-size:12px;padding:2px 0">
            <span style="font-weight:700;font-size:16px;color:var(--foreground)">${score}</span>
            <span style="color:var(--muted-foreground);margin-left:4px">Opportunity Score</span><br/>
            <span style="color:var(--muted-foreground);font-size:10px">Tract ${tractId}</span>
          </div>`,
        )
      }
      if (!added) {
        hoverPopup.addTo(map)
        added = true
      }
    }

    const handleLeave = () => {
      map.getCanvas().style.cursor = ''
      shownKey = null
      added = false
      hoverPopup.remove()
    }

    map.on('mousemove', LAYER_FILL_ID, handleMove)
    map.on('mouseleave', LAYER_FILL_ID, handleLeave)

    // Cleanup: remove layers + source on unmount; reset ref so a remount re-inits
    return () => {
      initializedRef.current = false
      hoverPopup.remove()
      map.off('mousemove', LAYER_FILL_ID, handleMove)
      map.off('mouseleave', LAYER_FILL_ID, handleLeave)
      // BEAD-0037: skip layer/source teardown if MapView's map.remove() already ran on
      // sign-out (getLayer would throw on the dropped style and blank the app).
      if (isMapAlive(map)) {
        if (map.getLayer(LAYER_OUTLINE_ID)) map.removeLayer(LAYER_OUTLINE_ID)
        if (map.getLayer(LAYER_FILL_ID)) map.removeLayer(LAYER_FILL_ID)
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID)
      }
    }
  }, [map])

  // -------------------------------------------------------------------------
  // T-012/W1: visibility toggle — show/hide both fill + outline layers.
  // Guarded on initializedRef so it only runs after the layers exist.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!map || !initializedRef.current) return
    const vis = visible ? 'visible' : 'none'
    if (map.getLayer(LAYER_FILL_ID)) map.setLayoutProperty(LAYER_FILL_ID, 'visibility', vis)
    if (map.getLayer(LAYER_OUTLINE_ID)) map.setLayoutProperty(LAYER_OUTLINE_ID, 'visibility', vis)
  }, [map, visible])

  // -------------------------------------------------------------------------
  // T-012/W4: tier + type filter. Apply a MapLibre `setFilter` so users can hide
  // low-opportunity tiers or non-relevant land-use types without a refetch.
  // When every tier AND every type is selected, the filter is cleared (null) so
  // the layer shows all features (no filtering overhead).
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!map || !initializedRef.current) return

    const allTiersOn = !tierFilter || tierFilter.length >= TRACT_TIERS.length
    const allTypesOn = !typeFilter || typeFilter.length >= TRACT_TYPES.length

    const filter =
      allTiersOn && allTypesOn
        ? null
        : [
            'all',
            ['in', TIER_EXPR, ['literal', tierFilter ?? [...TRACT_TIERS]]],
            ['in', ['get', 'dominant_type'], ['literal', typeFilter ?? [...TRACT_TYPES]]],
          ]

    if (map.getLayer(LAYER_FILL_ID)) map.setFilter(LAYER_FILL_ID, filter as never)
    if (map.getLayer(LAYER_OUTLINE_ID)) map.setFilter(LAYER_OUTLINE_ID, filter as never)
  }, [map, tierFilter, typeFilter])

  // -------------------------------------------------------------------------
  // Fetch + update source data whenever bbox changes (MAP-04 on load, on move)
  // AbortController cancels the previous in-flight request on every bbox change
  // so a stale slow response can never overwrite a fresher one (zoom flicker).
  // -------------------------------------------------------------------------
  useEffect(() => {
    // T-010: only fetch while visible. At low zoom the tract tier is hidden (county tier
    // is active), so skipping the fetch is the whole point of the LOD tier — otherwise the
    // expensive full-viewport tract retrieval would still fire on every moveend. Mirrors
    // CountyLayer's visible-gated fetch.
    if (!map || !bbox || !visible) {
      // A stale "showing the first N" notice must not outlive the tier that caused it.
      if (!visible) onTruncatedChangeRef.current?.(false, 0)
      return
    }

    const controller = new AbortController()
    const [west, south, east, north] = bbox
    const params = new URLSearchParams({
      south: String(south),
      west: String(west),
      north: String(north),
      east: String(east),
    })
    // T-010: zoom-driven geometry detail (simplified vs full). Omitted ⇒ API default.
    if (detail) params.set('detail', detail)

    const timer = setTimeout(() => {
      authedFetch(`/api/tracts/bbox?${params}`, { signal: controller.signal })
        .then((r) => {
          if (!r.ok) throw new Error(`tracts/bbox ${r.status}`)
          return r.json()
        })
        .then((data: unknown) => {
          if (controller.signal.aborted) return
          const fc = data as TractFeatureCollection
          const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined
          source?.setData(fc as GeoJSON.FeatureCollection)
          // T-275/F3: the API flags a capped viewport so the client can say so.
          onTruncatedChangeRef.current?.(fc.truncated === true, fc.features?.length ?? 0)
        })
        .catch((err: unknown) => {
          if (err instanceof Error && err.name !== 'AbortError') console.error(err)
        })
    }, DEBOUNCE_MS)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [map, bbox, detail, visible])

  // -------------------------------------------------------------------------
  // Optional: map click → onTractClick callback
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!map || !onTractClick) return

    const handler = (e: MapLayerMouseEvent) => {
      const feature = e.features?.[0]
      if (!feature?.properties) return
      const p = feature.properties as Record<string, unknown>
      onTractClick({
        tractId: String(p['tract_geoid'] ?? ''),
        lviScore: Number(p['lvi'] ?? 0),
        cdpiScore: Number(p['cdpi'] ?? 0),
        cmpiScore: Number(p['cmpi'] ?? 0),
        ppiScore: Number(p['ppi_v11'] ?? 0),
        gapScore: Number(p['gap_score'] ?? 0),
        dominantType: String(p['dominant_type'] ?? ''),
        lviPctileState: p['lvi_pctile_state'] != null ? Number(p['lvi_pctile_state']) : null,
      })
    }

    map.on('click', LAYER_FILL_ID, handler)
    return () => {
      map.off('click', LAYER_FILL_ID, handler)
    }
  }, [map, onTractClick])

  return null
}
